import { FC } from "react";
import { useTranslation } from "react-i18next";
import { DiscountBrandType } from "src/Modules/Discounts/types";
import { aggregatedData } from "../../constants";
import ImageWithEmptyFallback from "src/components/ImageWithEmptyFallback/ImageWithEmptyFallback";
import { Link } from "react-router-dom";
import { track } from "@amplitude/analytics-browser";

type DiscountsCardProps = Partial<DiscountBrandType> & {
  loading?: boolean;
  onClick?: () => void;
  code: string;
};

const GiftCard: FC<DiscountsCardProps> = ({ imageUrl, name, items = [], loading, code }) => {
  const { t } = useTranslation();
  const rawCategory = items[0]?.categoriesList?.[0];
  const category =
    rawCategory && typeof rawCategory === "object"
      ? (rawCategory as { name: string }).name
      : ((rawCategory as string | undefined) ?? items[0]?.categories?.[0]);
  if (loading) {
    return (
      <div className="relative font-inter overflow-hidden rounded-xs bg-background-default p-3 shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex aspect-square w-full animate-pulse items-center justify-center overflow-hidden rounded-sm bg-background-subtle">
            <svg className="h-10 w-10 text-icon-subtle" fill="none" viewBox="0 0 24 24" stroke="currentcolor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div className="flex w-full flex-col items-start gap-2">
            <div className="h-5 w-2/3 animate-pulse rounded-sm bg-background-subtle md:h-6" />
            <div className="flex w-full flex-col items-start gap-1">
              <div className="h-3 w-1/3 animate-pulse rounded-sm bg-background-subtle" />
              <div className="h-4 w-1/2 animate-pulse rounded-sm bg-background-subtle md:h-5" />
            </div>
          </div>
          <div className="absolute right-4 top-4 h-8 w-20 animate-pulse rounded-xs bg-background-hover" />
        </div>
      </div>
    );
  }

  return (
    <Link
      className="relative font-inter group relative overflow-hidden rounded-xs bg-background-default p-3 shadow-sm transition-shadow hover:shadow-xs"
      to={`${code}`}
      onClick={() => track("Card Selected", { code, name, category })}
    >
      <div className="flex flex-col items-center justify-center gap-3">
        <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-sm">
          <ImageWithEmptyFallback
            src={imageUrl}
            alt={name}
            className="h-full! w-full! object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </div>

        <div className="flex w-full flex-col items-start gap-2">
          <div className="text-font-default w-full truncate text-sm font-medium capitalize md:text-base">{name}</div>
          <div className="flex flex-col items-start justify-start">
            <div className="text-font-subtle text-xs leading-tight font-medium">{t("Giftcards.redemptionCost")}</div>
            <div className="text-font-default text-sm leading-tight md:text-base">{aggregatedData.points.labelTransform(items, t)}</div>
          </div>
        </div>
        {category && (
          <div className="absolute right-4 top-4 rounded-xs! font-semibold bg-background-hover px-3 py-2 text-xs text-font-active text-right max-w-3/4 truncate capitalize" title={category}>{category}</div>
        )}
      </div>
    </Link>
  );
};

export default GiftCard;
